import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';

export default function AdminBankApprovalView() {
  const [methods, setMethods] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterDate, setFilterDate] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const sendEmail = httpsCallable(functions, 'sendBankMethodStatusEmail');

  useEffect(() => {
    const fetchMethods = async () => {
      try {
        const q = query(collection(db, 'payoutMethods'), orderBy('submittedAt', 'desc'));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMethods(data);
      } catch (err) {
        console.error('❌ Failed to fetch payout methods:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMethods();
  }, []);

  const handleUpdateStatus = async (methodId, userId, status) => {
    const method = methods.find(m => m.id === methodId);
    if (!method) return;

    try {
      await updateDoc(doc(db, 'payoutMethods', methodId), {
        status,
        reviewedAt: serverTimestamp(),
      });

      await addDoc(collection(db, 'admin_logs'), {
        type: 'BankMethodReview',
        action: `${status} payout method`,
        methodId,
        targetUserId: userId,
        status,
        timestamp: serverTimestamp(),
      });

      await sendEmail({
        email: method.email,
        name: method.name || '',
        status,
        reason: status === 'Rejected' ? 'Your bank information could not be verified.' : '',
      });

      setMethods(prev =>
        prev.map(m => (m.id === methodId ? { ...m, status } : m))
      );
    } catch (err) {
      console.error('❌ Error updating status:', err);
    }
  };

  const filtered = methods.filter(method => {
    const matchesText =
      method.name?.toLowerCase().includes(filterText.toLowerCase()) ||
      method.email?.toLowerCase().includes(filterText.toLowerCase()) ||
      method.accountNumber?.includes(filterText);

    const matchesStatus = filterStatus === 'All' || method.status === filterStatus;

    const matchesDate =
      !filterDate ||
      (method.submittedAt?.toDate?.() &&
        new Date(method.submittedAt.toDate()) >= new Date(filterDate));

    return matchesText && matchesStatus && matchesDate;
  });

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>← Back to Dashboard</button>
      <h2 style={styles.header}>🏦 Bank Payout Approvals ({filtered.length})</h2>

      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Search name, email or account number..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          style={styles.search}
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.select}>
          <option value="All">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <input
          type="date"
          value={filterDate}
          onChange={e => setFilterDate(e.target.value)}
          style={styles.dateInput}
        />
      </div>

      {loading ? (
        <p>Loading bank methods...</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Submitted</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Routing</th>
              <th style={styles.th}>Account #</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(method => (
              <tr key={method.id}>
                <td style={styles.td}>{method.submittedAt?.toDate?.().toLocaleString() || '—'}</td>
                <td style={styles.td}>{method.name || '—'}</td>
                <td style={styles.td}>{method.email || '—'}</td>
                <td style={styles.td}>{method.routingNumber || '—'}</td>
                <td style={styles.td}>{method.accountNumber || '—'}</td>
                <td style={styles.td}>{method.status || 'Pending'}</td>
                <td style={styles.td}>
                  <button style={styles.approveBtn} onClick={() => handleUpdateStatus(method.id, method.userId, 'Approved')}>Approve</button>
                  <button style={styles.rejectBtn} onClick={() => handleUpdateStatus(method.id, method.userId, 'Rejected')}>Reject</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', fontFamily: 'system-ui, sans-serif', backgroundColor: '#f8f9fa', minHeight: '100vh' },
  backButton: { marginBottom: '1rem', padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #ccc', backgroundColor: '#eee', cursor: 'pointer' },
  header: { fontSize: '1.5rem', marginBottom: '1rem', color: '#0f5132' },
  filters: { display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' },
  search: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc', width: '100%', maxWidth: '300px' },
  select: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc', minWidth: '180px' },
  dateInput: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', boxShadow: '0 0 10px rgba(0,0,0,0.05)' },
  th: { borderBottom: '2px solid #ccc', padding: '0.75rem', textAlign: 'left', backgroundColor: '#e6f2ff' },
  td: { borderBottom: '1px solid #ddd', padding: '0.75rem', verticalAlign: 'top' },
  approveBtn: { padding: '0.25rem 0.75rem', marginRight: '0.5rem', backgroundColor: '#198754', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  rejectBtn: { padding: '0.25rem 0.75rem', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
};
